import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, Form, InputGroup, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/common/PageHeader';
import Loader from '../components/common/Loader';
import EmptyState from '../components/common/EmptyState';
import { reportApi } from '../api';
import { useToast } from '../context/ToastContext';
import { formatAge, formatDate, timeAgo, titleCase, toInputDate } from '../utils/format';

/** Moves a "2026-08-12" string by whole days without timezone surprises. */
function shiftDay(dayKey, delta) {
  const [y, m, d] = dayKey.split('-').map(Number);
  return toInputDate(new Date(y, m - 1, d + delta));
}

export default function FollowUps() {
  const navigate = useNavigate();
  const toast = useToast();

  const today = toInputDate(new Date());
  const [date, setDate] = useState(today);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reportApi.followUps({ date });
      setRows(res.data);
    } catch (err) {
      toast.error(err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [date, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const dayLabel =
    date === today
      ? 'today'
      : date === shiftDay(today, 1)
        ? 'tomorrow'
        : date === shiftDay(today, -1)
          ? 'yesterday'
          : formatDate(date);

  return (
    <>
      <PageHeader
        title="Follow-ups"
        subtitle="Pick a date to see who the doctor asked to come back that day"
        icon="bi-calendar-check"
      >
        <Button variant="outline-secondary" onClick={load} title="Refresh">
          <i className="bi bi-arrow-clockwise" />
        </Button>
      </PageHeader>

      <Card className="mb-4">
        <Card.Body className="d-flex flex-wrap align-items-center gap-3">
          <InputGroup style={{ width: 260 }}>
            <Button
              variant="outline-secondary"
              onClick={() => setDate((d) => shiftDay(d, -1))}
              aria-label="Previous day"
            >
              <i className="bi bi-chevron-left" />
            </Button>
            <Form.Control type="date" value={date} onChange={(e) => setDate(e.target.value || today)} />
            <Button
              variant="outline-secondary"
              onClick={() => setDate((d) => shiftDay(d, 1))}
              aria-label="Next day"
            >
              <i className="bi bi-chevron-right" />
            </Button>
          </InputGroup>

          <div className="d-flex gap-2 flex-wrap">
            <Button
              size="sm"
              variant={date === today ? 'primary' : 'outline-secondary'}
              onClick={() => setDate(today)}
            >
              Today
            </Button>
            <Button
              size="sm"
              variant={date === shiftDay(today, 1) ? 'primary' : 'outline-secondary'}
              onClick={() => setDate(shiftDay(today, 1))}
            >
              Tomorrow
            </Button>
          </div>

          <div className="ms-auto text-end">
            <div className="fw-bold fs-5 lh-1">{rows.length}</div>
            <div className="text-muted" style={{ fontSize: '0.78rem' }}>
              patient{rows.length === 1 ? '' : 's'} due {dayLabel}
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>Due on {formatDate(date)}</Card.Header>

        {loading ? (
          <Loader label="Looking up follow-ups..." />
        ) : rows.length === 0 ? (
          <EmptyState
            icon="bi-calendar-x"
            title={`Nobody is due on ${formatDate(date)}`}
            hint="Use the arrows or the date box to check another day."
          />
        ) : (
          <div className="table-responsive">
            <Table hover className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>Patient</th>
                  <th>Mobile</th>
                  <th className="d-none d-md-table-cell">Coming back for</th>
                  <th className="d-none d-lg-table-cell">Seen on</th>
                  <th className="text-end">Record</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((f) => (
                  <tr
                    key={f._id}
                    className="queue-row"
                    onClick={() => navigate(`/patients/${f.patient._id}`)}
                  >
                    <td>
                      <div className="list-name">{f.patient.name}</div>
                      <div className="list-sub">
                        {f.patient.patientId} &middot; {titleCase(f.patient.gender)}, {formatAge(f.patient)}
                      </div>
                    </td>

                    <td>
                      {/* Reception rings these numbers, so make them dialable. */}
                      <a
                        href={`tel:${f.patient.mobile}`}
                        className="fw-semibold"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {f.patient.mobile}
                      </a>
                    </td>

                    <td className="d-none d-md-table-cell">
                      {f.diagnosis?.length ? (
                        <span>{f.diagnosis.join(', ')}</span>
                      ) : (
                        <span className="text-muted">Review</span>
                      )}
                    </td>

                    <td className="d-none d-lg-table-cell small">
                      {formatDate(f.createdAt)}
                      <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                        {timeAgo(f.createdAt)}
                      </div>
                    </td>

                    <td className="text-end">
                      <Badge bg="secondary" className="me-2">
                        {f.patient.totalVisits} visit{f.patient.totalVisits === 1 ? '' : 's'}
                      </Badge>
                      <i className="bi bi-chevron-right list-chevron" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card>
    </>
  );
}
